import React from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { usePWAInstall } from '../hooks/usePWAInstall';

/* ── Ícones ─────────────────────────────────────────────── */
const ShareIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
        <polyline points="16 6 12 2 8 6" />
        <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
);

const AddBoxIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
);

const DotsIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <circle cx="5" cy="12" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="19" cy="12" r="2" />
    </svg>
);

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const ArrowDown: React.FC = () => (
    <div className="flex flex-col items-center gap-1 pwa-bounce-down">
        <div className="w-0.5 h-5 bg-primary rounded-full" />
        <svg viewBox="0 0 16 10" className="w-4 h-auto text-primary" fill="currentColor">
            <path d="M8 10L0 0h16L8 10z" />
        </svg>
    </div>
);

const ArrowUp: React.FC = () => (
    <div className="flex flex-col items-center gap-1 pwa-bounce-up">
        <svg viewBox="0 0 16 10" className="w-4 h-auto text-primary" fill="currentColor">
            <path d="M8 0L16 10H0L8 0z" />
        </svg>
        <div className="w-0.5 h-5 bg-primary rounded-full" />
    </div>
);

/* ── Sub-componentes reutilizáveis ───────────────────────── */
const SheetHandle: React.FC = () => (
    <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
);

const CloseBtn: React.FC<{ onClick: () => void; style?: React.CSSProperties }> = ({ onClick, style }) => (
    <button
        onClick={onClick}
        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-200 transition-colors"
        aria-label="Fechar"
        style={style}
    >
        <X size={16} />
    </button>
);

const AppHeader: React.FC = () => (
    <div className="flex items-center gap-3 mb-4">
        <img src="/logo.png" alt="Cognitya" className="w-11 h-11 rounded-2xl shadow" />
        <div>
            <h3 className="font-bold text-secondary text-base leading-tight">Instalar Cognitya</h3>
            <p className="text-xs text-gray-500">Acesso rápido na sua tela inicial</p>
        </div>
    </div>
);

interface StepProps {
    num: number;
    active?: boolean;
    label: React.ReactNode;
    sub?: React.ReactNode;
    icon: React.ReactNode;
}

const Step: React.FC<StepProps> = ({ num, active, label, sub, icon }) => (
    <div className={`flex items-center gap-3 rounded-2xl p-3 ${active ? 'bg-blue-50' : 'bg-gray-50'}`}>
        <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 text-white ${active ? 'bg-primary' : 'bg-gray-300'}`}>
            {num}
        </div>
        <div className="flex-1">
            <p className="text-sm font-semibold text-secondary">{label}</p>
            {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        </div>
        <div className={`w-7 h-7 rounded-xl p-1.5 shrink-0 ${active ? 'bg-blue-100' : 'bg-gray-100'}`}>
            {icon}
        </div>
    </div>
);

/* ── Componente principal ────────────────────────────────── */
export const PWAInstallPrompt: React.FC = () => {
    const { platform, isVisible, triggerInstall, dismiss, dismissPermanent } = usePWAInstall();

    if (!isVisible) return null;

    // ── Android: prompt nativo via beforeinstallprompt ──────────────────────────
    if (platform === 'android') {
        return (
            <>
                <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm" onClick={dismiss} />
                <div className="fixed bottom-0 inset-x-0 z-[201] pwa-slide-up">
                    <div className="bg-white rounded-t-3xl shadow-2xl px-6 pt-5 pb-10 max-w-lg mx-auto relative">
                        <SheetHandle />
                        <CloseBtn onClick={dismiss} />

                        <div className="flex items-center gap-4 mb-5">
                            <div className="w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                                <img src="/logo.png" alt="Cognitya" className="w-10 h-10 rounded-xl" />
                            </div>
                            <div>
                                <h3 className="font-bold text-secondary text-lg leading-tight">Instalar Cognitya</h3>
                                <p className="text-sm text-gray-500 mt-0.5">Acesso rápido direto da tela inicial</p>
                            </div>
                        </div>

                        <div className="flex gap-3 mb-5">
                            <div className="flex-1 flex flex-col items-center gap-1.5 bg-gray-50 rounded-2xl p-3 text-center">
                                <Smartphone size={20} className="text-primary" />
                                <span className="text-xs font-medium text-gray-600">Funciona offline</span>
                            </div>
                            <div className="flex-1 flex flex-col items-center gap-1.5 bg-gray-50 rounded-2xl p-3 text-center">
                                <Download size={20} className="text-primary" />
                                <span className="text-xs font-medium text-gray-600">Sem App Store</span>
                            </div>
                        </div>

                        <button
                            onClick={triggerInstall}
                            className="w-full h-12 bg-primary text-white font-semibold rounded-2xl shadow-lg shadow-primary/30 hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                        >
                            <Download size={18} />
                            Instalar aplicativo
                        </button>
                        <button onClick={dismiss} className="w-full mt-3 text-sm text-gray-400 hover:text-gray-600 transition-colors">
                            Agora não
                        </button>
                    </div>
                </div>
            </>
        );
    }

    // ── Android Manual (fallback quando beforeinstallprompt não disparar) ────────
    if (platform === 'android-manual') {
        return (
            <>
                <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm" onClick={dismiss} />
                <div className="fixed bottom-0 inset-x-0 z-[201] pwa-slide-up">
                    <div className="bg-white rounded-t-3xl shadow-2xl px-6 pt-5 pb-10 max-w-lg mx-auto relative">
                        <SheetHandle />
                        <CloseBtn onClick={dismiss} />

                        {/* Header com logo */}
                        <div className="flex items-center gap-4 mb-5">
                            <div className="w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                                <img src="/logo.png" alt="Cognitya" className="w-10 h-10 rounded-xl" />
                            </div>
                            <div>
                                <h3 className="font-bold text-secondary text-lg leading-tight">Instalar Cognitya</h3>
                                <p className="text-sm text-gray-500 mt-0.5">Siga os 3 passos abaixo</p>
                            </div>
                        </div>

                        {/* Cards de benefícios */}
                        <div className="flex gap-3 mb-5">
                            <div className="flex-1 flex flex-col items-center gap-1.5 bg-gray-50 rounded-2xl p-3 text-center">
                                <Smartphone size={20} className="text-primary" />
                                <span className="text-xs font-medium text-gray-600">Funciona offline</span>
                            </div>
                            <div className="flex-1 flex flex-col items-center gap-1.5 bg-gray-50 rounded-2xl p-3 text-center">
                                <Download size={20} className="text-primary" />
                                <span className="text-xs font-medium text-gray-600">Sem App Store</span>
                            </div>
                        </div>

                        {/* Passos */}
                        <div className="space-y-2 mb-4">
                            <Step num={1} active
                                label={<>Toque em <strong className="text-primary">⋮</strong> (menu)</>}
                                sub="Canto superior direito do Chrome"
                                icon={<DotsIcon className="w-full h-full text-primary" />}
                            />
                            <Step num={2}
                                label={<>Selecione <strong>Adicionar à tela inicial</strong></>}
                                sub="Role o menu até encontrar a opção"
                                icon={<AddBoxIcon className="w-full h-full text-gray-500" />}
                            />
                            <Step num={3}
                                label={<>Confirme tocando em <strong>Adicionar</strong></>}
                                sub="Uma caixa de confirmação vai aparecer"
                                icon={<span className="text-base font-bold text-gray-400 flex items-center justify-center w-full h-full">✓</span>}
                            />
                        </div>

                        <button onClick={dismiss} className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors py-2">
                            Agora não
                        </button>
                    </div>
                </div>
            </>
        );
    }


    // ── iOS In-App Browser ───────────────────────────────────────────────────────
    if (platform === 'ios-inapp') {
        return (
            <div className="fixed inset-x-0 top-0 z-[200] p-4 pwa-slide-down-from-top">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl shadow-xl p-4 max-w-sm mx-auto flex items-start gap-3 relative">
                    <button onClick={dismiss} className="absolute top-3 right-3 text-amber-400 hover:text-amber-600" aria-label="Fechar"><X size={16} /></button>
                    <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                        <Smartphone size={18} className="text-amber-600" />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-amber-800">Abra no Safari</p>
                        <p className="text-xs text-amber-700 mt-0.5 leading-snug">
                            Para instalar o Cognitya na tela inicial, abra este link no <strong>Safari</strong>.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ── iOS Safari — seta aponta para a BASE da tela ────────────────────────────
    if (platform === 'ios-safari') {
        return (
            <>
                <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm" onClick={dismiss} />
                <div className="fixed bottom-0 inset-x-0 z-[201] pwa-slide-up">
                    <div className="bg-white rounded-t-3xl shadow-2xl px-5 pt-5 pb-3 max-w-lg mx-auto relative">
                        <SheetHandle />
                        <CloseBtn onClick={dismiss} />
                        <AppHeader />
                        <div className="space-y-2 mb-3">
                            <Step num={1} active
                                label={<>Toque em <strong className="text-primary">Compartilhar</strong></>}
                                sub="Ícone na barra inferior do Safari"
                                icon={<ShareIcon className="w-full h-full text-primary" />}
                            />
                            <Step num={2}
                                label={<>Role a lista para baixo</>}
                                sub="Até encontrar as opções de lista"
                                icon={<ChevronDownIcon className="w-full h-full text-gray-500" />}
                            />
                            <Step num={3}
                                label={<>Toque em <strong>Adicionar à Tela de Início</strong></>}
                                sub={<>Toque no ícone <AddBoxIcon className="inline w-3 h-3 align-middle" /></>}
                                icon={<AddBoxIcon className="w-full h-full text-gray-500" />}
                            />
                        </div>
                        <button onClick={dismiss} className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors py-2">Agora não</button>
                        <div className="flex justify-center mt-1 mb-1"><ArrowDown /></div>
                        <div style={{ height: 'env(safe-area-inset-bottom, 12px)' }} />
                    </div>
                </div>
            </>
        );
    }

    // ── iOS Chrome — seta aponta para o TOPO da tela ────────────────────────────
    if (platform === 'ios-chrome') {
        return (
            <>
                <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm" onClick={dismiss} />
                <div className="fixed top-0 inset-x-0 z-[201] pwa-slide-down-from-top">
                    <div
                        className="bg-white rounded-b-3xl shadow-2xl px-5 pb-5 max-w-lg mx-auto relative"
                        style={{ paddingTop: `calc(env(safe-area-inset-top, 16px) + 16px)` }}
                    >
                        <CloseBtn onClick={dismiss} style={{ top: `calc(env(safe-area-inset-top, 12px) + 12px)` }} />
                        <div className="flex justify-center mb-2"><ArrowUp /></div>
                        <AppHeader />
                        <div className="space-y-2">
                            <Step num={1} active
                                label={<>Toque em <strong className="text-primary">Compartilhar</strong></>}
                                sub="Canto superior direito, na barra de endereço"
                                icon={<ShareIcon className="w-full h-full text-primary" />}
                            />
                            <Step num={2}
                                label={<>Toque em <strong>Ver Mais</strong></>}
                                sub="Ou role a página para ver a lista"
                                icon={<ChevronDownIcon className="w-full h-full text-gray-500" />}
                            />
                            <Step num={3}
                                label={<>Toque em <strong>Adicionar à Tela de Início</strong></>}
                                sub={<>Procure o ícone <AddBoxIcon className="inline w-3 h-3 align-middle" /></>}
                                icon={<AddBoxIcon className="w-full h-full text-gray-500" />}
                            />
                        </div>
                        <button onClick={dismiss} className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors py-3 mt-2">Agora não</button>
                        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto" />
                    </div>
                </div>
            </>
        );
    }

    return null;
};
